import { isTauri } from './platform';

interface AppBuildVersion {
  buildId: string;
  buildTime: string;
  gitCommit: string;
  name: string;
  version: string;
}

declare const __MES_BUILD_VERSION__: AppBuildVersion | undefined;

const VERSION_FILE_NAME = 'version.json';
const CHECK_INTERVAL_MS = 5 * 60 * 1000;
const RELOAD_COOLDOWN_MS = 2 * 60 * 1000;
const RELOAD_STATE_KEY = 'mes:update:last-reload';
const UPDATE_BROADCAST_KEY = 'mes:update:detected';
const REFRESH_QUERY_KEYS = ['__mes_v', '__mes_t'];

interface ReloadState {
  reason: string;
  targetBuildId: string;
  timestamp: number;
}

interface UpdateBroadcast {
  targetBuildId: string;
  timestamp: number;
}

function getCurrentBuildVersion(): AppBuildVersion | undefined {
  // 运行时该全局未必被 define 注入（如 dev），直接引用会 ReferenceError，
  // 必须用 typeof 探测
  return (
    // eslint-disable-next-line unicorn/no-typeof-undefined
    typeof __MES_BUILD_VERSION__ === 'undefined'
      ? undefined
      : __MES_BUILD_VERSION__
  );
}

/**
 * 获取当前构建版本信息（错误上报 buildId / sourcemap 版本对齐等场景使用）
 */
export function getAppBuildVersion(): AppBuildVersion | undefined {
  return getCurrentBuildVersion();
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function readString(record: Record<string, unknown>, key: string): string {
  const value = record[key];
  return typeof value === 'string' ? value : '';
}

function normalizeVersionPayload(value: unknown): AppBuildVersion | undefined {
  if (!isRecord(value)) return;

  const buildId = readString(value, 'buildId');
  if (!buildId) return;

  return {
    buildId,
    buildTime: readString(value, 'buildTime'),
    gitCommit: readString(value, 'gitCommit'),
    name: readString(value, 'name'),
    version: readString(value, 'version'),
  };
}

function getVersionFileUrl(): string {
  const base = import.meta.env.BASE_URL || '/';
  const normalizedBase = base.endsWith('/') ? base : `${base}/`;
  const baseUrl = new URL(normalizedBase, window.location.origin);
  const url = new URL(VERSION_FILE_NAME, baseUrl);
  url.searchParams.set('t', Date.now().toString());
  return url.toString();
}

function requestVersionFile(url: string): Promise<undefined | unknown> {
  return new Promise((resolve) => {
    try {
      const request = new XMLHttpRequest();

      // Keep this best-effort check off window.fetch. Browser extensions often
      // hook fetch and can emit noisy unhandled rejections for network blips.
      request.open('GET', url, true);
      request.timeout = 10_000;
      request.setRequestHeader('Accept', 'application/json');

      request.addEventListener('load', () => {
        if (request.status < 200 || request.status >= 300) {
          resolve(undefined);
          return;
        }

        try {
          resolve(JSON.parse(request.responseText) as unknown);
        } catch {
          resolve(undefined);
        }
      });
      request.addEventListener('error', () => resolve(undefined));
      request.addEventListener('timeout', () => resolve(undefined));
      request.addEventListener('abort', () => resolve(undefined));
      request.send();
    } catch {
      resolve(undefined);
    }
  });
}

async function fetchLatestVersion(): Promise<AppBuildVersion | undefined> {
  try {
    return normalizeVersionPayload(
      await requestVersionFile(getVersionFileUrl()),
    );
  } catch {}
}

function getLastReloadState(): ReloadState | undefined {
  try {
    const raw = window.sessionStorage.getItem(RELOAD_STATE_KEY);
    if (!raw) return;

    const parsed = JSON.parse(raw) as unknown;
    if (!isRecord(parsed)) return;

    const timestamp = parsed.timestamp;
    if (typeof timestamp !== 'number') return;

    return {
      reason: readString(parsed, 'reason'),
      targetBuildId: readString(parsed, 'targetBuildId'),
      timestamp,
    };
  } catch {}
}

function markReload(targetBuildId: string, reason: string): void {
  try {
    const state: ReloadState = {
      reason,
      targetBuildId,
      timestamp: Date.now(),
    };
    window.sessionStorage.setItem(RELOAD_STATE_KEY, JSON.stringify(state));
  } catch {
    // Ignore storage failures in private mode or constrained WebViews.
  }
}

function hasReloadedRecently(targetBuildId: string): boolean {
  const state = getLastReloadState();
  return (
    !!state &&
    state.targetBuildId === targetBuildId &&
    Date.now() - state.timestamp < RELOAD_COOLDOWN_MS
  );
}

async function clearCacheStorage(): Promise<void> {
  if (!('caches' in window)) return;

  try {
    const cacheNames = await window.caches.keys();
    await Promise.all(
      cacheNames.map((cacheName) => window.caches.delete(cacheName)),
    );
  } catch {
    // Cache Storage may be blocked by browser policy.
  }
}

async function unregisterServiceWorkers(): Promise<void> {
  if (!('serviceWorker' in navigator)) return;

  try {
    const registrations = await navigator.serviceWorker.getRegistrations();
    await Promise.all(
      registrations.map(async (registration) => {
        try {
          await registration.update();
          await registration.unregister();
        } catch {
          // Continue with the rest of the registrations.
        }
      }),
    );
  } catch {
    // Service worker APIs may be unavailable in some embedded browsers.
  }
}

function navigateToFreshEntry(targetBuildId: string): void {
  const url = new URL(window.location.href);
  url.searchParams.set('__mes_v', targetBuildId);
  url.searchParams.set('__mes_t', Date.now().toString());
  window.location.replace(url.toString());
}

function removeRefreshQueryParams(): void {
  const url = new URL(window.location.href);
  let changed = false;

  for (const key of REFRESH_QUERY_KEYS) {
    if (url.searchParams.has(key)) {
      url.searchParams.delete(key);
      changed = true;
    }
  }

  if (changed) {
    window.history.replaceState(
      window.history.state,
      document.title,
      `${url.pathname}${url.search}${url.hash}`,
    );
  }
}

function broadcastUpdate(targetBuildId: string): void {
  try {
    const payload: UpdateBroadcast = {
      targetBuildId,
      timestamp: Date.now(),
    };
    window.localStorage.setItem(UPDATE_BROADCAST_KEY, JSON.stringify(payload));
  } catch {
    // Cross-tab broadcast is best effort.
  }
}

async function reloadWithFreshAssets(
  reason: string,
  targetBuildId = getCurrentBuildVersion()?.buildId || 'unknown',
  shouldBroadcast = true,
): Promise<void> {
  if (hasReloadedRecently(targetBuildId)) return;

  markReload(targetBuildId, reason);

  if (shouldBroadcast) {
    broadcastUpdate(targetBuildId);
  }

  await Promise.all([clearCacheStorage(), unregisterServiceWorkers()]);
  navigateToFreshEntry(targetBuildId);
}

async function checkForAppUpdate(reason: string): Promise<void> {
  const currentVersion = getCurrentBuildVersion();
  if (!currentVersion?.buildId) return;

  const latestVersion = await fetchLatestVersion();
  if (
    !latestVersion?.buildId ||
    latestVersion.buildId === currentVersion.buildId
  ) {
    return;
  }

  // 版本变更不再静默整页刷新：正在填写的表单会被无提示销毁。
  // 发版提醒由 vben CheckUpdates 弹窗承接（preferences.app.enableCheckUpdates，
  // 用户确认后才 reload）；旧资源失效仍由下方 chunk 加载失败的自愈刷新兜底。
  if (import.meta.env.DEV) {
    // eslint-disable-next-line no-console
    console.info(
      `[app-update] 检测到新版本 ${latestVersion.buildId}（当前 ${currentVersion.buildId}，来源 ${reason}），等待用户确认刷新`,
    );
  }
}

function isChunkLoadMessage(message: string): boolean {
  const normalizedMessage = message.toLowerCase();
  return [
    'chunkloaderror',
    'css_chunk_load_failed',
    'error loading dynamically imported module',
    'failed to fetch dynamically imported module',
    'failed to load module script',
    'importing a module script failed',
    'loading chunk',
  ].some((text) => normalizedMessage.includes(text));
}

function getResourceUrl(target: EventTarget | null): string {
  if (target instanceof HTMLScriptElement) return target.src;
  if (target instanceof HTMLLinkElement) return target.href;
  return '';
}

function isVersionedAsset(url: string): boolean {
  if (!url) return false;

  try {
    const parsedUrl = new URL(url, window.location.href);
    if (parsedUrl.origin !== window.location.origin) return false;

    return (
      /\/(?:css|js|jse)\//.test(parsedUrl.pathname) ||
      /-[a-f0-9]{8,}\.(?:css|js)$/i.test(parsedUrl.pathname)
    );
  } catch {
    return false;
  }
}

function setupChunkLoadRecovery(): void {
  window.addEventListener('vite:preloadError', (event) => {
    event.preventDefault();
    void reloadWithFreshAssets('vite-preload-error');
  });

  window.addEventListener('unhandledrejection', (event) => {
    const reason = event.reason;
    const message =
      reason instanceof Error ? reason.message : String(reason || '');

    if (isChunkLoadMessage(message)) {
      event.preventDefault();
      void reloadWithFreshAssets('dynamic-import-error');
    }
  });

  window.addEventListener(
    'error',
    (event) => {
      const resourceUrl = getResourceUrl(event.target);
      if (!isVersionedAsset(resourceUrl)) return;

      void reloadWithFreshAssets('versioned-asset-error');
    },
    true,
  );
}

function setupUpdateBroadcastListener(): void {
  window.addEventListener('storage', (event) => {
    if (event.key !== UPDATE_BROADCAST_KEY || !event.newValue) return;

    try {
      const payload = JSON.parse(event.newValue) as unknown;
      if (!isRecord(payload)) return;

      const targetBuildId = readString(payload, 'targetBuildId');
      const currentBuildId = getCurrentBuildVersion()?.buildId;
      if (!targetBuildId || targetBuildId === currentBuildId) return;

      void reloadWithFreshAssets('version-broadcast', targetBuildId, false);
    } catch {
      // Ignore malformed broadcast payloads.
    }
  });
}

function setupVersionPolling(): void {
  void checkForAppUpdate('startup');

  window.setInterval(() => {
    if (!document.hidden) {
      void checkForAppUpdate('interval');
    }
  }, CHECK_INTERVAL_MS);

  window.addEventListener('visibilitychange', () => {
    if (!document.hidden) {
      void checkForAppUpdate('visible');
    }
  });

  window.addEventListener('online', () => {
    void checkForAppUpdate('online');
  });

  window.addEventListener('pageshow', (event) => {
    if (event.persisted) {
      void checkForAppUpdate('pageshow');
    }
  });
}

export function setupAppUpdateGuard(): void {
  if (typeof window === 'undefined') return;

  removeRefreshQueryParams();
  setupChunkLoadRecovery();

  if (import.meta.env.DEV || isTauri()) return;

  setupUpdateBroadcastListener();
  setupVersionPolling();
}
